import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Check,
  Clock,
  Boxes,
  Sparkles,
} from 'lucide-react';
import { ShopItem, GalleryImage, BundleItemDetail, DetailModalItem } from '../../types';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useShopStore } from '../../stores/useShopStore';
import { getItemGradient, getRarityConfig } from '../../utils/rarity';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';
import {
  RawShopEntry,
  parseRawEntryItem,
  calculateCardBackground,
} from '../../utils/fortniteShopParser';
import { cn } from '../../utils/cn';

export interface ItemDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: DetailModalItem | ShopItem | null;
  onAddToCart?: (item: ShopItem) => void;
  isInCart?: boolean;
  onToggleCart?: (item: ShopItem) => void;
}

/**
 * Format ISO outDate into Spanish Fortnite date string "DD/MM/AA"
 */
export function formatExitDate(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  try {
    const a = new Date(dateStr);
    if (isNaN(a.getTime())) return null;
    const s = new Date(Math.round(a.getTime() / 60000) * 60000);
    const pad = (w: number) => String(w).padStart(2, '0');
    const day = pad(s.getDate());
    const month = pad(s.getMonth() + 1);
    const year = String(s.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  } catch {
    return null;
  }
}

/**
 * Checks if the item expires within the next 24 hours
 */
export function isLeavingToday(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  try {
    const outTime = new Date(dateStr).getTime();
    const hours = (outTime - Date.now()) / (1000 * 60 * 60);
    return hours >= 0 && hours <= 24;
  } catch {
    return false;
  }
}

/**
 * Returns hours remaining until departure or null if unparseable
 */
export function getDepartureHours(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  try {
    const outTime = new Date(dateStr).getTime();
    if (isNaN(outTime)) return null;
    return (outTime - Date.now()) / (1000 * 60 * 60);
  } catch {
    return null;
  }
}

/**
 * Extracts all multi-images from item assets (renderImages, bundle, tracks, cars, brItems)
 */
export function extractGalleryImages(
  item: DetailModalItem | ShopItem | null
): GalleryImage[] {
  if (!item) return [];

  const raw =
    (item as DetailModalItem).rawEntry ||
    (typeof window !== 'undefined'
      ? useShopStore.getState().getItemByOfferId(item.id)
      : undefined);

  const list: GalleryImage[] = [];
  const visited = new Set<string>();

  const addImage = (url?: string, label: string = 'Vista', tag: string = '') => {
    if (
      !url ||
      typeof url !== 'string' ||
      !url.startsWith('http') ||
      url.includes('character_spiretend/featured.png') ||
      visited.has(url)
    ) {
      return;
    }
    visited.add(url);
    list.push({ url, label, tag });
  };

  // 1. Direct explicit gallery images
  if ((item as DetailModalItem).galleryImages?.length) {
    (item as DetailModalItem).galleryImages!.forEach((img) =>
      addImage(img.url, img.label, img.tag)
    );
  }

  // 2. Extract from raw Fortnite API newDisplayAsset.renderImages
  if (raw?.newDisplayAsset?.renderImages?.length) {
    const renderImages = [...raw.newDisplayAsset.renderImages]
      .filter((r: any) => r && r.image)
      .sort((a: any, b: any) => {
        const isLegoA = a.productTag === 'Product.Juno' ? 1 : 0;
        const isLegoB = b.productTag === 'Product.Juno' ? 1 : 0;
        return isLegoA - isLegoB;
      });

    renderImages.forEach((render: any, idx: number) => {
      const isLego = render.productTag === 'Product.Juno';
      const label = isLego
        ? 'Estilo LEGO'
        : renderImages.length > 1
        ? `Vista ${idx + 1}`
        : 'Principal';
      addImage(render.image, label, render.productTag || '');
    });
  }

  // 3. Extract from raw bundle image
  if (raw?.bundle?.image) {
    addImage(raw.bundle.image, 'Lote');
  }

  // 4. Extract from cars
  if (raw?.cars?.length) {
    raw.cars.forEach((car: any) => {
      const carImg = car.images?.large || car.images?.small;
      if (carImg) addImage(carImg, car.name || 'Vehículo', 'car');
    });
  }

  // 5. Extract from tracks
  if (raw?.tracks?.length) {
    raw.tracks.forEach((track: any) => {
      if (track.albumArt) addImage(track.albumArt, track.title || 'Pista');
    });
  }

  // 6. Extract from brItems
  if (raw?.brItems?.length) {
    raw.brItems.forEach((sub: any) => {
      const subImg =
        sub.images?.featured ||
        sub.images?.large ||
        sub.images?.icon ||
        sub.images?.smallIcon;
      if (subImg) addImage(subImg, sub.name || 'Accesorio');
    });
  }

  // 7. Extract from standard ShopItem images
  if (item.images) {
    if (item.images.featured) addImage(item.images.featured, 'Destacado');
    if (item.images.icon) addImage(item.images.icon, 'Principal');
    if (item.images.background) addImage(item.images.background, 'Fondo');
  }

  // Fallback to direct image
  if (item.image) {
    addImage(item.image, item.name || 'Principal');
  }

  return list;
}

/**
 * Extracts bundle item breakdown (brItems, cars, tracks, etc.)
 */
export function extractBundleItems(
  item: DetailModalItem | ShopItem | null
): BundleItemDetail[] {
  if (!item) return [];

  // Direct explicit breakdown
  if ((item as DetailModalItem).bundleItems?.length) {
    return (item as DetailModalItem).bundleItems!;
  }

  const raw =
    (item as DetailModalItem).rawEntry ||
    (typeof window !== 'undefined'
      ? useShopStore.getState().getItemByOfferId(item.id)
      : undefined);

  if (!raw) return [];

  const items: BundleItemDetail[] = [];

  if (raw.brItems?.length) {
    raw.brItems.forEach((bi: any) => {
      items.push({
        id: bi.id,
        name: bi.name || 'Objeto',
        type: bi.type?.displayValue || bi.type?.value || 'Cosmético',
        image:
          bi.images?.smallIcon ||
          bi.images?.icon ||
          bi.images?.featured ||
          bi.images?.large,
        rarity: bi.rarity?.displayValue || bi.rarity?.value || 'Raro',
      });
    });
  }

  if (raw.tracks?.length) {
    raw.tracks.forEach((t: any) => {
      items.push({
        id: t.id,
        name: t.title ? `${t.title} - ${t.artist || ''}` : 'Pista de Música',
        type: 'Pista de Música',
        image: t.albumArt,
        rarity: 'Poco Común',
      });
    });
  }

  if (raw.cars?.length) {
    raw.cars.forEach((c: any) => {
      items.push({
        id: c.id,
        name: c.name || 'Vehículo',
        type: c.type?.displayValue || 'Carrocería',
        image: c.images?.small || c.images?.large,
        rarity: c.rarity?.displayValue || 'Raro',
      });
    });
  }

  return items;
}

export const ItemDetailModal: React.FC<ItemDetailModalProps> = ({
  isOpen,
  onClose,
  item,
  onAddToCart,
  isInCart = false,
  onToggleCart,
}) => {
  // Global Currency Store
  const calculatePrice = useCurrencyStore((s) => s.calculatePrice);
  const formatPrice = useCurrencyStore((s) => s.formatPrice);

  // Gallery and UI state
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Swipe handling
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  // Raw entry resolution
  const rawEntry = useMemo<RawShopEntry | undefined>(() => {
    if (!item) return undefined;
    if ((item as DetailModalItem).rawEntry) {
      return (item as DetailModalItem).rawEntry as RawShopEntry;
    }
    return useShopStore.getState().getItemByOfferId(item.id);
  }, [item]);

  const parsedShopItem = useMemo(() => {
    return rawEntry ? parseRawEntryItem(rawEntry) : null;
  }, [rawEntry]);

  // Reset states on item change
  useEffect(() => {
    setActiveImageIndex(0);
    setImgLoaded(false);
  }, [item?.id]);

  // Gallery items extraction
  const gallery = useMemo(() => extractGalleryImages(item), [item]);
  const bundleItems = useMemo(() => extractBundleItems(item), [item]);

  // Background Gradient: official Fortnite calculation if rawEntry available, else fallback
  const bgGradient = useMemo(() => {
    try {
      if (rawEntry && parsedShopItem) {
        return calculateCardBackground(rawEntry, parsedShopItem);
      }
      return item ? getItemGradient(item) : 'linear-gradient(180deg, #1447e6 0%, #06173f 100%)';
    } catch {
      return item ? getItemGradient(item) : 'linear-gradient(180deg, #1447e6 0%, #06173f 100%)';
    }
  }, [rawEntry, parsedShopItem, item]);

  const activeImage = gallery[activeImageIndex] || gallery[0] || {
    url: item?.images?.icon || item?.image || '',
    label: item?.name || 'Cosmético',
  };

  // Safe Close with exit animation (matching 140ms anim-modal-out)
  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    // Restore scroll lock immediately so user can resume buttery smooth scrolling without waiting
    unlockScroll();
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 140);
  }, [isClosing, onClose]);

  // Keyboard navigation (Escape, ArrowLeft, ArrowRight)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      } else if (gallery.length > 1) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setActiveImageIndex((prev) => (prev + 1) % gallery.length);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setActiveImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, gallery.length, handleClose]);

  // Lock scroll and register modal-open class for floating widgets suppression
  useEffect(() => {
    if (isOpen) {
      lockScroll();

      return () => {
        unlockScroll();
      };
    }
  }, [isOpen]);

  // Touch Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches && e.touches.length > 0) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const diffX = touchStartX.current - e.changedTouches[0].clientX;
    const diffY = touchStartY.current - e.changedTouches[0].clientY;

    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 40 && gallery.length > 1) {
      if (diffX > 0) {
        setActiveImageIndex((prev) => (prev + 1) % gallery.length);
      } else {
        setActiveImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // ALL HOOKS COMPLETE. Safe to return early if modal is not open.
  if (!isOpen || !item) return null;

  // Pricing calculations
  const finalPrice = item.finalPrice ?? item.vbucks ?? 0;
  const fiatPrice = calculatePrice(finalPrice, item.id);
  const formattedFiat = formatPrice(fiatPrice);

  const rarityConfig = getRarityConfig(item);
  const isBundle = Boolean(item.isBundle || item.category?.toUpperCase() === 'LOTES');
  const outDate = (item as DetailModalItem).outDate || rawEntry?.outDate;
  const exitDateFormatted = formatExitDate(outDate);
  const leavingToday = isLeavingToday(outDate);
  const departureHours = getDepartureHours(outDate);
  const urgencyColorClass =
    departureHours !== null && departureHours <= 24
      ? 'text-red-600'
      : departureHours !== null && departureHours <= 48
      ? 'text-orange-500'
      : 'text-[#06173f]';
  const isVehicle =
    item.category?.toUpperCase() === 'VEHÍCULOS' ||
    item.category?.toUpperCase() === 'CARS' ||
    Boolean(rawEntry?.cars && rawEntry.cars.length > 0);

  // Resolved metadata strings
  const rarityText =
    parsedShopItem?.rarity?.displayValue ||
    rarityConfig.name ||
    item.rarity ||
    'Poco Común';

  const typeText =
    parsedShopItem?.type?.displayValue ||
    (isBundle ? 'Lote' : item.category || 'Cosmético');

  const seriesText =
    parsedShopItem?.series?.value ||
    item.series ||
    rawEntry?.brItems?.[0]?.series?.value;

  const setText =
    (item as DetailModalItem).set ||
    rawEntry?.brItems?.[0]?.set?.text ||
    rawEntry?.brItems?.[0]?.set?.value;

  const introductionText =
    (item as DetailModalItem).introduction ||
    rawEntry?.brItems?.[0]?.introduction?.text;

  const rawDesc = item.description || '';
  const isGenericDesc =
    !rawDesc ||
    rawDesc.trim().toLowerCase() === 'bundle' ||
    rawDesc.trim().toLowerCase() === 'conjunto de objetos' ||
    rawDesc.trim().toLowerCase() === 'lote' ||
    rawDesc.trim() === '';
  const displayDescription = isGenericDesc ? null : rawDesc;

  const handleActionClick = () => {
    if (onToggleCart) {
      onToggleCart(item);
    } else if (onAddToCart) {
      onAddToCart(item);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={item.name}
      className={cn(
        'fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-5 md:p-6',
        'soft-backdrop select-none overscroll-contain',
        isClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
      )}
      onClick={handleClose}
    >
      {/* Modal Dialog Shell with smooth 60fps spring pop-in and fast fade-out */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative w-full flex flex-col md:flex-row shadow-[0_20px_50px_rgba(6,23,63,0.7)]',
          'max-h-[88vh] md:max-h-[82vh] md:h-[82vh]',
          isBundle ? 'max-w-5xl' : 'max-w-4xl',
          isClosing ? 'anim-modal-out' : 'anim-modal-in'
        )}
      >
        {/* Chamfered Box Container with official border */}
        <div
          className="w-full h-full bg-white border-2 border-[var(--g-line)] flex flex-col md:flex-row overflow-hidden relative"
          style={{ clipPath: 'var(--g-cut)' }}
        >
          {/* Close Button (Esc) - Sleek translucent glass button */}
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar modal (Esc)"
            title="Cerrar (Esc)"
            className="absolute top-3 right-3 z-30 w-8 h-8 md:w-9 md:h-9 rounded-full bg-black/45 backdrop-blur-[4px] hover:bg-black/70 text-white border border-white/20 transition-all cursor-pointer shadow-lg active:scale-95 flex items-center justify-center"
          >
            <X className="w-4 h-4 md:w-5 md:h-5 stroke-[2.5]" />
          </button>

          {/* LEFT COLUMN: Gallery & Main Cosmetic Viewer */}
          <div
            className={cn(
              'w-full flex flex-col shrink-0 md:shrink md:h-full relative min-h-0 overflow-hidden',
              'md:border-r-2 border-[var(--g-line)]',
              isBundle ? 'md:w-[55%]' : 'md:w-1/2'
            )}
          >
            {/* Asset Display Box (Full-Bleed, zoomed cosmetic) */}
            <div
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              style={{ background: isVehicle ? '#0c0c0e' : bgGradient }}
              className={cn(
                'relative flex items-center justify-center overflow-hidden touch-pan-y min-h-0',
                isBundle ? 'h-64 sm:h-72 md:h-auto' : 'h-60 sm:h-68 md:h-auto',
                'md:flex-1 p-2 sm:p-4'
              )}
            >
              {/* Vignette Shadow Overlay */}
              {!isVehicle && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
              )}

              {/* Main Image with Zoom */}
              <img
                src={activeImage?.url || item.image || item.images?.icon || ''}
                alt={activeImage?.label || item.name || 'Cosmético'}
                draggable={false}
                onLoad={() => setImgLoaded(true)}
                className={cn(
                  'relative z-10 max-h-full max-w-full object-contain select-none pointer-events-none transform scale-110 sm:scale-115 md:scale-120 transition-all duration-200',
                  'drop-shadow-[0_12px_28px_rgba(0,0,0,0.45)]',
                  imgLoaded ? 'opacity-100' : 'opacity-0'
                )}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    item.image ||
                    item.images?.icon ||
                    'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=500&auto=format&fit=crop';
                }}
              />

              {/* Navigation Arrows: Translucent glass buttons */}
              {gallery.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length)
                    }
                    aria-label="Imagen anterior"
                    title="Anterior"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-9 md:h-9 rounded-full bg-black/45 backdrop-blur-[4px] hover:bg-black/70 text-white border border-white/20 flex items-center justify-center transition shadow-md cursor-pointer active:scale-95"
                  >
                    <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveImageIndex((prev) => (prev + 1) % gallery.length)
                    }
                    aria-label="Siguiente imagen"
                    title="Siguiente"
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-9 md:h-9 rounded-full bg-black/45 backdrop-blur-[4px] hover:bg-black/70 text-white border border-white/20 flex items-center justify-center transition shadow-md cursor-pointer active:scale-95"
                  >
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </button>
                </>
              )}

              {/* Subtle bottom gradient for organic transition */}
              <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/50 via-black/15 to-transparent pointer-events-none z-10" />
            </div>

            {/* Thumbnail Navigation Strip (without horizontal table divider, no cutoff) */}
            {gallery.length > 1 && (
              <div
                ref={thumbnailContainerRef}
                onWheel={(e) => {
                  e.currentTarget.scrollLeft += e.deltaY;
                }}
                className="px-3 py-2 md:py-2.5 bg-slate-50/90 flex items-center justify-start gap-2.5 overflow-x-auto no-scrollbar shrink-0"
              >
                {gallery.map((img, idx) => {
                  const isActive = idx === activeImageIndex;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      title={img.label || `Vista ${idx + 1}`}
                      className={cn(
                        'relative w-12 h-12 md:w-14 md:h-14 shrink-0 p-1 rounded-lg border transition-all duration-150',
                        'overflow-hidden flex items-center justify-center cursor-pointer bg-[#F1F5F9]',
                        isActive
                          ? 'border-2 border-[var(--g-royal)] ring-2 ring-[var(--g-royal)]/25 shadow-md opacity-100 scale-105'
                          : 'border-slate-200/90 hover:border-slate-400 opacity-80 hover:opacity-100'
                      )}
                    >
                      <img
                        src={img.url}
                        alt="thumbnail"
                        draggable={false}
                        className="w-full h-full object-contain select-none pointer-events-none"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Metadata, Breakdown & Actions */}
          <div
            className={cn(
              'w-full flex flex-col flex-1 md:h-full min-h-0 overflow-hidden bg-white text-[var(--g-ink)]',
              isBundle ? 'md:w-[45%]' : 'md:w-1/2'
            )}
          >
            {/* Scrollable Information Body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6 space-y-3.5 min-h-0 overscroll-contain">
              {/* Badge Chips Row */}
              <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                {/* Lote Chip */}
                {isBundle && (
                  <span
                    className="badge-chip bg-amber-400 text-[var(--g-ink)] font-chakra font-black text-[9px] md:text-[10px] italic shadow-sm px-2 py-0.5"
                    style={{ clipPath: 'var(--g-slash)' }}
                  >
                    LOTE
                  </span>
                )}

                {/* Urgency Badge */}
                {leavingToday && (
                  <span
                    className="badge-chip bg-red-600 text-white font-chakra font-black text-[9px] md:text-[10px] shadow-sm flex items-center gap-1 px-2 py-0.5"
                    style={{ clipPath: 'var(--g-slash)' }}
                  >
                    <Clock className="w-2.5 h-2.5" />
                    Se va hoy
                  </span>
                )}

                {/* Series Badge */}
                {seriesText && (
                  <span
                    className="badge-chip bg-[var(--g-royal)] text-white font-chakra font-bold text-[9px] md:text-[10px] shadow-sm px-2.5 py-0.5"
                    style={{ clipPath: 'var(--g-slash)' }}
                  >
                    {seriesText}
                  </span>
                )}

                {/* Rarity Badge with vibrant rarity background */}
                <span
                  className="badge-chip font-chakra font-black text-[9px] md:text-[10px] px-2.5 py-0.5 uppercase tracking-wider text-white shadow-xs rounded-xs"
                  style={{
                    backgroundColor: rarityConfig.tagColor || '#9333ea',
                  }}
                >
                  {rarityText}
                </span>

                {/* Type Badge in neutral gray (e.g. COMPAÑERO / TRAJE) */}
                <span
                  className="badge-chip bg-slate-100 text-slate-600 border border-slate-200/80 font-chakra font-bold text-[9px] md:text-[10px] px-2.5 py-0.5 uppercase tracking-wider rounded-xs"
                >
                  {typeText}
                </span>
              </div>

              {/* Title, Set Header & Organic Description */}
              <div className="space-y-1">
                <h2 className="text-2xl md:text-3xl font-chakra font-black uppercase italic tracking-tight text-[var(--g-ink)] leading-tight">
                  {item.name}
                </h2>
                {setText && (
                  <p className="text-xs text-[var(--g-royal)] font-chakra font-bold uppercase tracking-wider">
                    {setText}
                  </p>
                )}
                {/* Description directly as dark gray italic with left accent bar (no bulky box) */}
                {displayDescription && (
                  <p className="text-xs md:text-sm text-slate-700 italic font-barlow font-medium leading-relaxed border-l-[3px] border-[#CBD5E1] pl-3 py-0.5 mt-2">
                    “{displayDescription}”
                  </p>
                )}
              </div>

              {/* Introduction Details */}
              {introductionText && (
                <p className="text-[11px] text-slate-500 italic font-barlow">
                  {introductionText}
                </p>
              )}

              {/* Shop Departure Countdown Note (No pill, dynamic urgency color) */}
              {exitDateFormatted && (
                <div className={`flex items-center gap-1.5 text-xs font-chakra font-bold pt-1 ${urgencyColorClass}`}>
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  <span>Se va de la tienda {exitDateFormatted}</span>
                </div>
              )}

              {/* Bundle Breakdown Grid */}
              {bundleItems.length > 1 && (
                <div className="space-y-2 pt-3 border-t-2 border-[var(--g-line)]">
                  <span className="text-xs font-chakra font-black uppercase tracking-wider text-[var(--g-ink)] flex items-center gap-2">
                    <Boxes className="w-4 h-4 text-[var(--g-royal)]" />
                    Contenido ({bundleItems.length} objetos)
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {bundleItems.map((sub, sIdx) => (
                      <div
                        key={sub.id || sIdx}
                        className="flex items-center gap-2.5 p-2 bg-[var(--g-ice)] border border-[var(--g-line)] hover:border-[var(--g-royal)] transition"
                        style={{ clipPath: 'var(--g-slash)' }}
                      >
                        {sub.image ? (
                          <img
                            src={sub.image}
                            alt={sub.name}
                            draggable={false}
                            className="w-10 h-10 object-contain bg-white border border-[var(--g-line)] p-1 shrink-0 select-none pointer-events-none"
                            style={{ clipPath: 'var(--g-cut)' }}
                          />
                        ) : (
                          <div
                            className="w-10 h-10 bg-white border border-[var(--g-line)] flex items-center justify-center text-[var(--g-royal)] shrink-0"
                            style={{ clipPath: 'var(--g-cut)' }}
                          >
                            <Sparkles className="w-4 h-4" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-[var(--g-ink)] truncate font-chakra">
                            {sub.name}
                          </p>
                          <p className="text-[10px] text-[var(--g-soft)] truncate font-barlow">
                            {sub.type || 'Accesorio'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ACTION & PRICING BAR (Sticky Footer - #F8FAFC Subtle Contrast) */}
            <div className="p-4 md:p-5 bg-[#F8FAFC] border-t border-slate-200 flex items-center justify-between gap-3 shrink-0">
              {/* Dual Price: Inverted hierarchy - Fiat primary, V-Bucks secondary (no '≈') */}
              <div className="flex flex-col min-w-0">
                <span className="text-xl md:text-2xl font-chakra font-black text-[#0F172A] tracking-tight leading-tight">
                  {formattedFiat}
                </span>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-chakra font-semibold mt-0.5">
                  <div className="w-4 h-4 bg-yellow-400 text-black flex items-center justify-center text-[9px] font-black italic rounded-full shadow-xs shrink-0">
                    V
                  </div>
                  <span>{(finalPrice ?? 0).toLocaleString('es-AR')} pavos</span>
                </div>
              </div>

              {/* Action Button: Añadir / En el Carrito */}
              <button
                type="button"
                onClick={handleActionClick}
                className={cn(
                  'px-5 py-3 md:py-3.5 font-chakra font-black uppercase italic tracking-wider text-xs md:text-sm',
                  'flex items-center gap-2.5 transition-all active:scale-95 shadow-md cursor-pointer shrink-0',
                  isInCart
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-[var(--g-royal)] hover:bg-[var(--g-deep)] text-white'
                )}
                style={{ clipPath: 'var(--g-slash)' }}
              >
                {isInCart ? (
                  <>
                    <Check className="w-4 h-4 stroke-[3]" />
                    <span>En el Carrito</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" />
                    <span>Añadir al Carrito</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ItemDetailModal.displayName = 'ItemDetailModal';

export default ItemDetailModal;
